import React, { useState, useEffect } from 'react';
import { X, Search, Filter, ChevronDown, ChevronUp, Check, Square, CheckSquare } from 'lucide-react';
import { agriculturalAPI } from '../services/api';
import { FieldInfo } from '../services/dataAnalyzer';

interface FilterValue {
  value: string;
  count: number;
}

interface AdvancedFilterPanelProps {
  indexName: string;
  fields: FieldInfo[];
  filters: { [key: string]: any };
  onFiltersChange: (filters: { [key: string]: any }) => void;
  onApply: () => void;
  currentData?: any[]; // Données actuelles pour filtrer les valeurs disponibles
  onLoadingChange?: (loading: boolean) => void; // Callback pour exposer l'état de chargement
}

const AdvancedFilterPanel: React.FC<AdvancedFilterPanelProps> = ({
  indexName,
  fields,
  filters,
  onFiltersChange,
  onApply,
  currentData = [],
  onLoadingChange,
}) => {
  const [filterValues, setFilterValues] = useState<{ [key: string]: FilterValue[] }>({});
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState<{ [key: string]: boolean }>({});
  const [searchTerms, setSearchTerms] = useState<{ [key: string]: string }>({});
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: Set<string> }>({});

  useEffect(() => {
    if (indexName) {
      loadFilterValues();
    }
  }, [indexName, currentData]);

  // Exposer l'état de chargement au parent
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loadingFilters);
    }
  }, [loadingFilters, onLoadingChange]);

  useEffect(() => {
    // Initialiser les valeurs sélectionnées depuis les filtres
    const initialSelected: { [key: string]: Set<string> } = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        if (Array.isArray(filters[key])) {
          initialSelected[key] = new Set(filters[key]);
        } else {
          initialSelected[key] = new Set([filters[key]]);
        }
      }
    });
    setSelectedValues(initialSelected);
  }, [filters]);

  const loadFilterValues = async () => {
    const allFilterValues: { [key: string]: FilterValue[] } = {};
    
    try {
      setLoadingFilters(true);
      
      const filterableFields = fields.filter((f) => f.canBeFilter && !f.isCode);
      
      // Détecter si c'est un index QCL (crops/livestock)
      const isQCL = indexName.toLowerCase().includes('crops') || 
                    indexName.toLowerCase().includes('livestock') ||
                    indexName.toLowerCase().includes('qcl');
      
      // Champs qui peuvent causer des erreurs 500 avec de grandes limites
      const problematicFields = ['year', 'flag', 'unit', 'value', 'note', 'created at', 'updated at', 'scraped at'];
      
      // Pour les champs importants (Area, Item, Element), utiliser getIndexTerms avec des limites élevées
      // Cela garantit qu'on récupère TOUTES les valeurs, pas seulement celles dans currentData
      for (const field of filterableFields) {
        const fieldName = field.name;
        const lowerFieldName = fieldName.toLowerCase();
        
        // Pour Area (countries), utiliser getIndexTerms avec une limite élevée
        if (lowerFieldName === 'area' || lowerFieldName.includes('country') || lowerFieldName.includes('region')) {
          try {
            const areaRes = await agriculturalAPI.getIndexTerms(indexName, {
              field: 'area',
              size: 5000, // Limite élevée pour récupérer tous les pays
            });
            if (areaRes.data.success && areaRes.data.data && Array.isArray(areaRes.data.data)) {
              const validAreas = areaRes.data.data.filter((term: FilterValue) => 
                term.value && term.value.trim() !== ''
              );
              if (validAreas.length > 0) {
                // Utiliser uniquement le nom du champ original, pas de doublons
                allFilterValues[fieldName] = validAreas;
              }
            }
          } catch (e) {
            console.warn(`Failed to load area terms for ${fieldName}:`, e);
          }
        }
        // Pour Item, utiliser getIndexTerms avec limite élevée
        else if (lowerFieldName === 'item') {
          try {
            const itemRes = await agriculturalAPI.getIndexTerms(indexName, {
              field: 'item',
              size: 2000,
            });
            if (itemRes.data.success && itemRes.data.data && Array.isArray(itemRes.data.data)) {
              const validItems = itemRes.data.data.filter((term: FilterValue) => 
                term.value && term.value.trim() !== ''
              );
              if (validItems.length > 0) {
                // Utiliser uniquement le nom du champ original, pas de doublons
                allFilterValues[fieldName] = validItems;
              }
            }
          } catch (e) {
            console.warn(`Failed to load item terms for ${fieldName}:`, e);
          }
        }
        // Pour Element (QCL), utiliser getIndexTerms
        else if (isQCL && lowerFieldName === 'element') {
          try {
            const elementRes = await agriculturalAPI.getIndexTerms(indexName, {
              field: 'element',
              size: 500,
            });
            if (elementRes.data.success && elementRes.data.data && Array.isArray(elementRes.data.data)) {
              const validElements = elementRes.data.data.filter((term: FilterValue) => 
                term.value && term.value.trim() !== ''
              );
              if (validElements.length > 0) {
                // Utiliser uniquement le nom du champ original, pas de doublons
                allFilterValues[fieldName] = validElements;
              }
            }
          } catch (e) {
            console.warn(`Failed to load element terms for ${fieldName}:`, e);
          }
        }
        // Pour Year, utiliser getIndexTerms avec limite raisonnable
        else if (lowerFieldName === 'year' || lowerFieldName.includes('year')) {
          try {
            const yearRes = await agriculturalAPI.getIndexTerms(indexName, {
              field: 'year',
              size: 200, // Limite raisonnable pour les années
            });
            if (yearRes.data.success && yearRes.data.data && Array.isArray(yearRes.data.data)) {
              const validYears = yearRes.data.data.filter((term: FilterValue) => 
                term.value && term.value.trim() !== ''
              );
              if (validYears.length > 0) {
                allFilterValues[fieldName] = validYears;
              }
            }
          } catch (e) {
            console.warn(`Failed to load year terms for ${fieldName}:`, e);
          }
        }
        // Pour Flag et Unit, utiliser getIndexTerms avec limite petite
        else if (lowerFieldName === 'flag' || lowerFieldName === 'unit') {
          try {
            const flagUnitRes = await agriculturalAPI.getIndexTerms(indexName, {
              field: lowerFieldName,
              size: 50, // Limite petite pour Flag et Unit
            });
            if (flagUnitRes.data.success && flagUnitRes.data.data && Array.isArray(flagUnitRes.data.data)) {
              const validValues = flagUnitRes.data.data.filter((term: FilterValue) => 
                term.value && term.value.trim() !== ''
              );
              if (validValues.length > 0) {
                allFilterValues[fieldName] = validValues;
              }
            }
          } catch (e) {
            console.warn(`Failed to load ${lowerFieldName} terms for ${fieldName}:`, e);
          }
        }
        
        // Si on n'a pas encore chargé ce champ, utiliser l'API standard
        // Vérifier aussi les variantes de nom (Area/area, Item/item, etc.)
        const normalizedFieldName = normalizeFieldName(fieldName);
        if (!allFilterValues[fieldName] && !allFilterValues[normalizedFieldName]) {
          try {
            // Essayer d'abord getAllIndexFilterValues avec limite élevée
            const response = await agriculturalAPI.getAllIndexFilterValues(indexName, 5000);
            
            if (response.data.success && response.data.data.filterValues) {
              // Chercher le champ sous différents noms possibles
              const possibleNames = [
                fieldName,
                normalizedFieldName,
                fieldName.charAt(0).toUpperCase() + fieldName.slice(1).toLowerCase(),
                fieldName.toLowerCase(),
                fieldName.toUpperCase()
              ];
              
              let found = false;
              for (const possibleName of possibleNames) {
                if (response.data.data.filterValues[possibleName]) {
                  allFilterValues[fieldName] = response.data.data.filterValues[possibleName].values || [];
                  found = true;
                  break;
                }
              }
              
              if (!found) {
                // Pour certains champs, utiliser des limites plus petites pour éviter les erreurs 500
                // Year, Flag, Unit peuvent avoir des problèmes avec de grandes limites
                const problematicFields = ['year', 'flag', 'unit', 'value', 'note'];
                const isProblematic = problematicFields.includes(lowerFieldName);
                const sizeLimit = isProblematic ? 500 : 2000;
                
                try {
                  const fieldResponse = await agriculturalAPI.getIndexFilterValues(indexName, fieldName, sizeLimit);
                  if (fieldResponse.data.success) {
                    allFilterValues[fieldName] = fieldResponse.data.data.values || [];
                  }
                } catch (fieldError: any) {
                  // Si erreur 500, essayer avec une limite encore plus petite
                  if (fieldError.response?.status === 500 && sizeLimit > 100) {
                    try {
                      const retryResponse = await agriculturalAPI.getIndexFilterValues(indexName, fieldName, 100);
                      if (retryResponse.data.success) {
                        allFilterValues[fieldName] = retryResponse.data.data.values || [];
                      }
                    } catch (retryError) {
                      console.warn(`Failed to load values for ${fieldName} even with smaller limit:`, retryError);
                    }
                  } else {
                    console.warn(`Failed to load values for ${fieldName}:`, fieldError);
                  }
                }
              }
            }
          } catch (e) {
            console.warn(`Failed to load values for ${fieldName}:`, e);
            
            // Dernier recours : extraire depuis currentData si disponible
            if (currentData && currentData.length > 0) {
              const valueCounts = new Map<string, number>();
              currentData.forEach((row) => {
                const value = row[fieldName] || row[normalizedFieldName];
                if (value !== null && value !== undefined && value !== '') {
                  const valueStr = String(value).trim();
                  if (valueStr) {
                    valueCounts.set(valueStr, (valueCounts.get(valueStr) || 0) + 1);
                  }
                }
              });
              
              allFilterValues[fieldName] = Array.from(valueCounts.entries())
                .map(([value, count]) => ({ value, count }))
                .sort((a, b) => b.count - a.count);
            }
          }
        }
      }
      
      setFilterValues(allFilterValues);
    } catch (err: any) {
      console.error('Failed to load filter values:', err);
      
      // Si certaines valeurs ont été chargées, les garder
      if (Object.keys(allFilterValues).length > 0) {
        setFilterValues(allFilterValues);
      }
      
      // En cas d'erreur, essayer d'extraire depuis currentData pour les champs manquants
      if (currentData && currentData.length > 0) {
        const allFilterValues: { [key: string]: FilterValue[] } = {};
        const filterableFields = fields.filter((f) => f.canBeFilter && !f.isCode);
        
        for (const field of filterableFields) {
          const fieldName = field.name;
          const valueCounts = new Map<string, number>();
          
          currentData.forEach((row) => {
            const value = row[fieldName];
            if (value !== null && value !== undefined && value !== '') {
              const valueStr = String(value).trim();
              if (valueStr) {
                valueCounts.set(valueStr, (valueCounts.get(valueStr) || 0) + 1);
              }
            }
          });
          
          allFilterValues[fieldName] = Array.from(valueCounts.entries())
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => b.count - a.count);
        }
        
        setFilterValues(allFilterValues);
      }
    } finally {
      setLoadingFilters(false);
    }
  };

  const toggleFilterExpansion = (fieldName: string) => {
    setExpandedFilters((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const handleSearchChange = (fieldName: string, value: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const toggleValueSelection = (fieldName: string, value: string) => {
    setSelectedValues((prev) => {
      const newSelected = { ...prev };
      if (!newSelected[fieldName]) {
        newSelected[fieldName] = new Set();
      }
      
      if (newSelected[fieldName].has(value)) {
        newSelected[fieldName].delete(value);
      } else {
        newSelected[fieldName].add(value);
      }
      
      // Mettre à jour les filtres
      const updatedFilters = { ...filters };
      if (newSelected[fieldName].size === 0) {
        delete updatedFilters[fieldName];
      } else if (newSelected[fieldName].size === 1) {
        updatedFilters[fieldName] = Array.from(newSelected[fieldName])[0];
      } else {
        updatedFilters[fieldName] = Array.from(newSelected[fieldName]);
      }
      
      onFiltersChange(updatedFilters);
      return newSelected;
    });
  };

  const clearFilter = (fieldName: string) => {
    setSelectedValues((prev) => {
      const newSelected = { ...prev };
      delete newSelected[fieldName];
      return newSelected;
    });
    
    const updatedFilters = { ...filters };
    delete updatedFilters[fieldName];
    onFiltersChange(updatedFilters);
  };

  // Select all values currently visible for a field (respects the active search term)
  const selectAllValues = (fieldName: string) => {
    const visibleValues = getFilteredValues(fieldName).map((v) => v.value);
    setSelectedValues((prev) => {
      const newSelected = { ...prev };
      newSelected[fieldName] = new Set(visibleValues);

      const updatedFilters = { ...filters };
      if (newSelected[fieldName].size === 0) {
        delete updatedFilters[fieldName];
      } else if (newSelected[fieldName].size === 1) {
        updatedFilters[fieldName] = visibleValues[0];
      } else {
        updatedFilters[fieldName] = visibleValues;
      }

      onFiltersChange(updatedFilters);
      return newSelected;
    });
  };

  const clearAllFilters = () => {
    setSelectedValues({});
    onFiltersChange({});
  };

  const getFilteredValues = (fieldName: string): FilterValue[] => {
    // Chercher les valeurs sous le nom exact ou des variantes
    const normalizedName = normalizeFieldName(fieldName);
    const possibleNames = [
      fieldName,
      normalizedName,
      fieldName.charAt(0).toUpperCase() + fieldName.slice(1).toLowerCase(),
      fieldName.toLowerCase(),
      fieldName.toUpperCase()
    ];
    
    let values: FilterValue[] = [];
    for (const name of possibleNames) {
      if (filterValues[name] && filterValues[name].length > 0) {
        values = filterValues[name];
        break;
      }
    }
    
    const searchTerm = searchTerms[fieldName]?.toLowerCase() || '';
    
    if (!searchTerm) return values;
    
    return values.filter((item) =>
      item.value.toLowerCase().includes(searchTerm)
    );
  };

  // Normaliser les noms de champs pour éviter les doublons (Area/area, Item/item, etc.)
  const normalizeFieldName = (name: string): string => {
    const lower = name.toLowerCase();
    // Normaliser vers la casse du champ dans fields
    const field = fields.find(f => f.name.toLowerCase() === lower);
    return field ? field.name : name;
  };

  // Filtrer et dédupliquer les champs filtrables
  const filterableFields = fields
    .filter((f) => f.canBeFilter && !f.isCode)
    .filter((field, index, self) => {
      // Garder uniquement la première occurrence de chaque champ (normalisé)
      return self.findIndex(f => f.name.toLowerCase() === field.name.toLowerCase()) === index;
    });

  const activeFiltersCount = Object.keys(selectedValues).filter(
    (key) => selectedValues[key] && selectedValues[key].size > 0
  ).length;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
          {loadingFilters && (
            <div className="flex items-center gap-2 px-2 py-1 bg-gradient-to-r from-emerald-100 to-emerald-50 text-emerald-700 rounded-full text-xs font-medium border border-emerald-300">
              <div className="w-3 h-3 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
              <span>Detecting...</span>
            </div>
          )}
          {!loadingFilters && activeFiltersCount > 0 && (
            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium border border-emerald-300">
              {activeFiltersCount} actif{activeFiltersCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {activeFiltersCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
          >
            <X className="h-4 w-4" />
            Tout effacer
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {filterableFields.map((field) => {
          const values = getFilteredValues(field.name);
          const isExpanded = expandedFilters[field.name] || false;
          const selected = selectedValues[field.name] || new Set();
          const hasSelection = selected.size > 0;

          return (
            <div
              key={field.name}
              className={`border rounded-lg transition-all ${
                hasSelection ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white'
              }`}
            >
              <button
                onClick={() => toggleFilterExpansion(field.name)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 text-left">
                  <div className={`p-1.5 rounded ${hasSelection ? 'bg-emerald-100 border border-emerald-300' : 'bg-gray-100 border border-gray-200'}`}>
                    {hasSelection ? (
                      <CheckSquare className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Square className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{field.displayName}</div>
                    <div className="text-xs text-gray-600">
                      {hasSelection
                        ? `${selected.size} selected`
                        : `${values.length} valeurs disponibles`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasSelection && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilter(field.name);
                      }}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400 transition-colors"
                      title="Effacer ce filtre"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-3">
                  {/* Search */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder={`Rechercher dans ${field.displayName}...`}
                      value={searchTerms[field.name] || ''}
                      onChange={(e) => handleSearchChange(field.name, e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900 placeholder-gray-500"
                    />
                  </div>

                  {/* Select all / Clear row */}
                  {!loadingFilters && values.length > 0 && (
                    <div className="flex items-center justify-between mb-2 px-1">
                      <span className="text-xs text-gray-600">
                        {selected.size > 0
                          ? `${selected.size} selected`
                          : `${values.length} value${values.length > 1 ? 's' : ''}`}
                        {searchTerms[field.name] ? ` matching "${searchTerms[field.name]}"` : ''}
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => selectAllValues(field.name)}
                          className="text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
                        >
                          Select all ({values.length})
                        </button>
                        {selected.size > 0 && (
                          <button
                            onClick={() => clearFilter(field.name)}
                            className="text-xs font-medium text-red-500 hover:text-red-600 hover:underline"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Values List */}
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {loadingFilters ? (
                      <div className="flex flex-col items-center justify-center py-8 space-y-4">
                        {/* Spinner animé moderne */}
                        <div className="relative">
                          {/* Cercle externe rotatif */}
                          <div className="w-16 h-16 border-4 border-emerald-200 rounded-full"></div>
                          {/* Cercle interne rotatif */}
                          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-emerald-500 rounded-full animate-spin"></div>
                          {/* Cercle moyen */}
                          <div className="absolute top-2 left-2 w-12 h-12 border-4 border-transparent border-r-emerald-600 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
                          {/* Point central */}
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full animate-pulse"></div>
                        </div>
                        {/* Texte avec animation */}
                        <div className="text-center space-y-1">
                          <p className="text-sm font-medium text-emerald-600 animate-pulse">
                            Detecting attributes...
                          </p>
                          <p className="text-xs text-gray-600">
                            Chargement des valeurs disponibles pour la filtration
                          </p>
                        </div>
                        {/* Barre de progression animée */}
                        <div className="w-full max-w-xs h-1 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-500 rounded-full animate-shimmer bg-[length:200%_100%]"></div>
                        </div>
                      </div>
                    ) : values.length === 0 ? (
                      <div className="text-center py-4 text-sm text-gray-600">
                        Aucune valeur disponible
                      </div>
                    ) : (
                      values.slice(0, 100).map((item) => {
                        const isSelected = selected.has(item.value);
                        return (
                          <button
                            key={item.value}
                            onClick={() => toggleValueSelection(field.name, item.value)}
                            className={`w-full px-3 py-2 text-left rounded-md text-sm transition-colors flex items-center justify-between ${
                              isSelected
                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {isSelected ? (
                                <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                              ) : (
                                <div className="h-4 w-4 flex-shrink-0" />
                              )}
                              <span className="truncate">{item.value}</span>
                            </div>
                            <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                              ({item.count})
                            </span>
                          </button>
                        );
                      })
                    )}
                    {values.length > 100 && (
                      <div className="text-xs text-gray-600 text-center py-2">
                        Showing first 100 of {values.length} results
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {activeFiltersCount > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={onApply}
            className="w-full px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-md hover:from-emerald-600 hover:to-emerald-700 transition-colors font-medium shadow-lg hover:shadow-emerald-500/50"
          >
            Appliquer les filtres ({activeFiltersCount})
          </button>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilterPanel;
